import { KEY_BOARD } from 'constants/general';
import { ButtonHTMLAttributes } from 'react';
import Tooltip from './Tooltip';
import InfoCircle from 'assets/icons/InfoCircle';

interface ToggleSwitchProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  checked?: boolean;
  handleToggle: (checked: boolean) => void;
  label?: string;
  tooltip?: string;
  labelStyles?: string;
  toggleDims?: string;
  toggleHandlerDims?: string;
}

const ToggleIcon = ({ isVisible }: { isVisible: boolean }) =>
  isVisible ? (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      fill='currentColor'
      viewBox='0 0 24 24'
      className={`absolute inset-0 w-full h-full p-1 transition-opacity text-gray-900 dark:text-secondary-dark-900 ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`}>
      <path d='M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z'></path>
    </svg>
  ) : (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      fill='none'
      viewBox='0 0 24 24'
      className={`absolute inset-0 w-full h-full p-1 transition-opacity text-gray-900 dark:text-secondary-dark-900 ${
        !isVisible ? 'opacity-100' : 'opacity-0'
      }`}>
      <path
        d='M18 6L6 18M6 6l12 12'
        stroke='currentColor'
        strokeWidth='2.5'
        strokeLinecap='round'
      />
    </svg>
  );

export const ToggleSwitch: React.FC<ToggleSwitchProps> = ({
  checked,
  handleToggle,
  label,
  className = 'flex items-center ~gap-2/2.5',
  disabled,
  tooltip,
  labelStyles = '~text-xs/base line-clamp-1',
  toggleDims = '~w-8/10 ~h-4/[1.375rem]',
  toggleHandlerDims = '~w-3/4 ~h-3/4',
  ...rest
}) => {
  return (
    <div className={`${className} cursor-pointer safari-flex-fix`}>
      <label className='flex items-center ~gap-1/2 flex-shrink-0'>
        <button
          type='button'
          role='switch'
          aria-checked={checked}
          aria-disabled={disabled}
          onClick={() => handleToggle(!checked)}
          onKeyDown={(event) => {
            if (
              [KEY_BOARD.ENTER.KEY, KEY_BOARD.SPACE.KEY].includes(event.key)
            ) {
              event.preventDefault();
              handleToggle(!checked);
            }
          }}
          className={`relative inline-block ~min-w-8/10 ~min-h-4/[1.375rem] ${toggleDims} transition duration-200 ease-in-out rounded-full border
            ${
              checked
                ? 'bg-primary-500 border-primary-500 shadow dark:bg-primary-dark-500 dark:border-primary-dark-100'
                : 'bg-primary-50 dark:bg-secondary-dark-400 border-primary-300/40 dark:border-secondary-dark-500 shadow-sm'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          disabled={disabled}
          {...rest}>
          <span
            className={`absolute left-1 top-1/2 -translate-y-1/2 ~min-w-3/4 ~min-h-3/4 ${toggleHandlerDims}  rounded-full transition-transform 
              ${checked ? 'translate-x-full bg-white dark:bg-secondary-dark-200' : 'translate-x-0 bg-primary-200 dark:bg-secondary-dark-300'}`}>
            <ToggleIcon isVisible={checked ?? false} />
          </span>
        </button>
        {label && <span className={labelStyles}>{label}</span>}
      </label>
      {tooltip && (
        <div className='flex-shrink-0'>
          <Tooltip
            message={tooltip}
            icon={<InfoCircle styles='~w-3.5/5 ~h-3.5/5' />}
          />
        </div>
      )}
    </div>
  );
};
